import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./RepairServiceText.module.scss";

interface ModeData {
  label: string;
  title: string;
  subtitle: string;
  descriptions: string[];
  processTitle: string;
  processList: string[];
  suitableTitle: string;
  suitableText: string;
}

const modeIcons: Record<string, string> = {
  reparatur: "/images/wrench 1.svg",
  austausch: "/images/exchange 1.svg",
};

const modeKeys = ["reparatur", "austausch"] as const;

const RepairServiceText: React.FC = () => {
  const { t } = useTranslation("common");
  const [activeTab, setActiveTab] = useState(0);

  const title = t("repairServiceText.title");
  const cta = t("repairServiceText.cta");

  const modes = modeKeys.map((key) => ({
    id: key,
    ...(t(`repairServiceText.modes.${key}`, {
      returnObjects: true,
    }) as ModeData),
  }));

  const renderColumn = (mode: (typeof modes)[number]) => {
    const iconSrc = modeIcons[mode.id];
    return (
      <>
        {/* Block 1: Icon + Title */}
        <div className={styles.block}>
          <div className={styles.icon}>
            {iconSrc && <img src={iconSrc} alt="" width={48} height={48} />}
          </div>
          <h3 className={styles.title}>{mode.title}</h3>
        </div>

        {/* Block 2: Subtitle + Descriptions */}
        <div className={styles.block}>
          <p className={styles.subtitle}>{mode.subtitle}</p>
          {mode.descriptions.map((desc, i) => (
            <p key={i} className={styles.desc}>
              {desc}
            </p>
          ))}
        </div>

        {/* Block 3: Process */}
        <div className={styles.block}>
          <h4 className={styles.processTitle}>{mode.processTitle}</h4>
          <ul className={styles.processList}>
            {mode.processList.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>

        {/* Block 4: Suitable */}
        <div className={styles.block}>
          <h4 className={styles.suitableTitle}>{mode.suitableTitle}</h4>
          <p className={styles.suitableText}>{mode.suitableText}</p>
        </div>
      </>
    );
  };

  return (
    <div className="container section">
      <div className={`sectionContainer sectionContainerWhite bigSection`}>
        {/* Title */}
        <div className="sideP64 titleCont">
          <div className="titleBlock w100">
            <h2>{title}</h2>
          </div>
        </div>

        {/* Segmented control — mobile only */}
        <div className={`${styles.segmented} sideP64`}>
          <div
            className={styles.segTrack}
            style={
              { "--count": modes.length, "--index": activeTab } as React.CSSProperties
            }
          >
            <div className={styles.segSlider} />
            {modes.map((mode, i) => (
              <button
                key={mode.id}
                className={`${styles.segOption} ${i === activeTab ? styles.isActive : ""}`}
                onClick={() => setActiveTab(i)}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>

        {/* Desktop: two columns */}
        <div className={`${styles.columns} sideP64`}>
          {modes.map((mode) => (
            <div key={mode.id} className={styles.column}>
              {renderColumn(mode)}
            </div>
          ))}
        </div>

        {/* Mobile: single column with tabs */}
        <div className={`${styles.mobileContent} sideP64`}>
          <div className={styles.column}>{renderColumn(modes[activeTab])}</div>
        </div>

        {/* CTA Banner */}
        <div className={`${styles.ctaWrap} sideP64`}>
          <div className={styles.cta}>
            <p>{cta}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepairServiceText;
